import { useMemo, useState, type FormEvent } from 'react';
import {
  COUNTDOWN_REPEATS,
  countdownDaysRemaining,
  isoDateInZone,
  type Countdown,
  type CountdownRepeat,
  type UpdateCountdownPayload,
} from '@cleartask/shared';
import { useCurrentUser } from '../../session.js';
import { ColorField } from '../../components/ui/ColorField.js';
import { IconField } from '../../components/ui/IconField.js';
import { useToast } from '../../components/ui/Toast.js';
import { CountdownCard } from './CountdownCard.js';
import {
  newCountdownId,
  useCountdowns,
  useCreateCountdown,
  useDeleteCountdown,
  useUpdateCountdown,
} from './useCountdowns.js';

/** O mesmo padrao do DDL, para o campo ja abrir com um valor. */
const COR_PADRAO_CONTAGEM = '#1B2C56';

const REPEAT_OPTION_PT: Record<CountdownRepeat, string> = {
  none: 'Não se repete',
  weekly: 'Toda semana',
  monthly: 'Todo mês',
  yearly: 'Todo ano',
};

/**
 * Contagens regressivas.
 *
 * A ordem e derivada, nao vem do servidor: quantos dias faltam depende de
 * `repeat` e de "hoje", e o SQLite nao sabe calcular nenhum dos dois. O
 * servidor devolve ordenado por data alvo apenas como ponto de partida estavel.
 */
export function CountdownList() {
  const user = useCurrentUser();
  const { data, isPending } = useCountdowns();
  const criar = useCreateCountdown();
  const atualizar = useUpdateCountdown();
  const remover = useDeleteCountdown();
  const { showToast } = useToast();

  /** Qual contagem esta com o formulario aberto — uma por vez. */
  const [emEdicao, setEmEdicao] = useState<string | null>(null);

  // O fuso autoritativo e o do usuario (spec 4.4), nunca o do dispositivo.
  const today = isoDateInZone(user.timezone, Date.now());

  const ordenadas = useMemo(() => {
    const lista = [...(data?.countdowns ?? [])];

    return lista.sort((a, b) => {
      const da = countdownDaysRemaining(a, today);
      const db = countdownDaysRemaining(b, today);

      // O que ja passou vai para o fim, e nao para o topo: uma data vencida em
      // 2019 nao e a informacao mais urgente da tela.
      if (da >= 0 && db < 0) return -1;
      if (da < 0 && db >= 0) return 1;
      return da >= 0 ? da - db : db - da;
    });
  }, [data, today]);

  function excluir(countdown: Countdown) {
    remover.mutate(countdown.id);

    // Desfazer = recriar com o MESMO id. Nao existe endpoint de undo, e o id
    // vem do cliente justamente para tornar isso possivel (spec 10.3).
    showToast({
      message: `“${countdown.title}” excluída.`,
      action: {
        label: 'Desfazer',
        onAction: () =>
          criar.mutate({
            id: countdown.id,
            title: countdown.title,
            icon: countdown.icon,
            targetDate: countdown.targetDate,
            repeat: countdown.repeat,
            color: countdown.color,
            // Sem estes dois, desfazer a exclusao devolveria a contagem sem o
            // aviso que ela tinha.
            reminderTime: countdown.reminderTime,
            reminderDaysBefore: countdown.reminderDaysBefore,
          }),
      },
    });
  }

  if (isPending) {
    return <p className="p-4 text-sm text-text-mut">Carregando contagens…</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      {ordenadas.length === 0 ? (
        <p className="rounded bg-surface p-4 text-center text-sm text-text-mut shadow-card">
          Nenhuma data marcada. Criar uma contagem →
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {ordenadas.map((countdown) =>
            // O formulario substitui o card, como na lista de habitos: sao
            // cinco campos curtos, e um modal esconderia as outras datas — que
            // sao a referencia de quem esta remarcando uma delas.
            emEdicao === countdown.id ? (
              <li key={countdown.id}>
                <FormularioDeContagem
                  idBase={countdown.id}
                  titulo={`Editar “${countdown.title}”`}
                  inicial={{
                    title: countdown.title,
                    targetDate: countdown.targetDate,
                    repeat: countdown.repeat,
                    icon: countdown.icon,
                    color: countdown.color,
                    reminderTime: countdown.reminderTime,
                    reminderDaysBefore: countdown.reminderDaysBefore,
                  }}
                  rotuloDoBotao="Salvar"
                  pendente={atualizar.isPending}
                  onCancel={() => setEmEdicao(null)}
                  onSubmit={(valores) => {
                    const patch = patchDeContagem(countdown, valores);
                    if (Object.keys(patch).length > 0) {
                      atualizar.mutate({ id: countdown.id, patch });
                    }
                    setEmEdicao(null);
                  }}
                />
              </li>
            ) : (
              <CountdownCard
                key={countdown.id}
                countdown={countdown}
                today={today}
                onEdit={() => setEmEdicao(countdown.id)}
                onDelete={() => excluir(countdown)}
              />
            ),
          )}
        </ul>
      )}

      <NovaContagem today={today} />
    </div>
  );
}

/** O que o formulario preenche — os mesmos campos na criacao e na edicao. */
interface ValoresDaContagem {
  title: string;
  targetDate: string;
  repeat: CountdownRepeat;
  icon: string | null;
  color: string;
  /** 'HH:MM' no fuso do usuario, ou null para nenhum aviso. */
  reminderTime: string | null;
  /** 0 = no dia, 1 = na vespera, e assim por diante. */
  reminderDaysBefore: number;
}

/**
 * O formulario de contagem, um so para criar e para editar.
 *
 * `idBase` existe porque o `ColorField` precisa de um id unico no documento, e
 * o formulario de edicao convive com o de criacao na mesma tela.
 */
function FormularioDeContagem({
  idBase,
  titulo,
  inicial,
  rotuloDoBotao,
  pendente,
  onSubmit,
  onCancel,
}: {
  idBase: string;
  titulo: string;
  inicial: ValoresDaContagem;
  rotuloDoBotao: string;
  pendente: boolean;
  onSubmit: (valores: ValoresDaContagem) => void;
  onCancel?: () => void;
}) {
  const [title, setTitle] = useState(inicial.title);
  const [targetDate, setTargetDate] = useState(inicial.targetDate);
  const [repeat, setRepeat] = useState<CountdownRepeat>(inicial.repeat);
  const [icon, setIcon] = useState<string | null>(inicial.icon);
  const [color, setColor] = useState(inicial.color);
  // Texto vazio = sem lembrete, como no formulario de habito.
  const [reminderTime, setReminderTime] = useState(inicial.reminderTime ?? '');
  // Texto enquanto esta na tela: apagar o numero para digitar outro nao pode
  // passar por um 0 que a pessoa nao escolheu.
  const [diasAntes, setDiasAntes] = useState(String(inicial.reminderDaysBefore));

  const antecedencia = Number(diasAntes);
  const antecedenciaInvalida =
    reminderTime !== '' &&
    (diasAntes.trim() === '' ||
      !Number.isInteger(antecedencia) ||
      antecedencia < 0 ||
      antecedencia > 365);

  function submeter(e: FormEvent) {
    e.preventDefault();
    const texto = title.trim();
    if (!texto || !targetDate || antecedenciaInvalida) return;

    onSubmit({
      title: texto,
      targetDate,
      repeat,
      icon,
      color,
      reminderTime: reminderTime === '' ? null : reminderTime,
      // Sem lembrete a antecedencia nao significa nada; volta ao padrao para
      // nao sobrar um "3 dias" esquecido que reapareceria ao religar o aviso.
      reminderDaysBefore: reminderTime === '' ? 0 : antecedencia,
    });
  }

  return (
    <form
      onSubmit={submeter}
      className="flex flex-col gap-3 rounded bg-surface p-3 shadow-card"
    >
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-text-mut">{titulo}</span>
        <div className="flex gap-2">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Audiência ANEEL"
            className="min-h-touch flex-1 rounded-sm border border-border bg-bg px-3"
          />
          <ColorField
            id={`countdown-color-${idBase}`}
            label="Cor da contagem"
            value={color}
            onChange={setColor}
          />
        </div>
      </label>

      <IconField label="Ícone" value={icon} onChange={setIcon} />

      <div className="flex gap-3">
        <label className="flex flex-1 flex-col gap-1 text-sm">
          <span className="text-text-mut">Data</span>
          <input
            type="date"
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
            className="min-h-touch rounded-sm border border-border bg-bg px-3"
          />
        </label>

        <label className="flex flex-1 flex-col gap-1 text-sm">
          <span className="text-text-mut">Repetição</span>
          <select
            value={repeat}
            onChange={(e) => setRepeat(e.target.value as CountdownRepeat)}
            className="min-h-touch rounded-sm border border-border bg-bg px-3"
          >
            {COUNTDOWN_REPEATS.map((valor) => (
              <option key={valor} value={valor}>
                {REPEAT_OPTION_PT[valor]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/*
        O aviso sai no dia da data (ou N dias antes), na hora escolhida, no
        fuso do usuario — uma vez por ocorrencia, inclusive nas que se repetem.
      */}
      <div className="flex gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1 text-sm">
          <label htmlFor={`countdown-reminder-${idBase}`} className="text-text-mut">
            Lembrete
          </label>
          <div className="flex gap-2">
            <input
              id={`countdown-reminder-${idBase}`}
              type="time"
              value={reminderTime}
              onChange={(e) => setReminderTime(e.target.value)}
              className="min-h-touch min-w-0 flex-1 rounded-sm border border-border bg-bg px-3"
            />
            {reminderTime !== '' ? (
              <button
                type="button"
                onClick={() => setReminderTime('')}
                className="min-h-touch rounded-sm px-3 text-sm text-text-mut hover:bg-surface-2"
              >
                Sem lembrete
              </button>
            ) : null}
          </div>
        </div>

        {/* Antecedencia so aparece com horario: sem aviso, ela nao muda nada. */}
        {reminderTime !== '' ? (
          <label className="flex w-24 flex-col gap-1 text-sm">
            <span className="text-text-mut">Dias antes</span>
            <input
              type="number"
              min={0}
              max={365}
              value={diasAntes}
              onChange={(e) => setDiasAntes(e.target.value)}
              className="min-h-touch rounded-sm border border-border bg-bg px-3"
            />
          </label>
        ) : null}
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={title.trim() === '' || antecedenciaInvalida || pendente}
          className="min-h-touch flex-1 rounded-sm bg-brand-blue px-4 font-medium text-white disabled:opacity-50"
        >
          {rotuloDoBotao}
        </button>

        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="min-h-touch rounded-sm px-4 text-sm text-text-mut hover:bg-surface-2"
          >
            Cancelar
          </button>
        ) : null}
      </div>
    </form>
  );
}

/**
 * Criacao com icone e cor.
 *
 * Os dois campos eram lidos e renderizados no card desde a fase 6 e nao tinham
 * onde ser preenchidos: toda contagem criada pela interface nascia com o
 * `#1B2C56` do DDL e sem icone. Mesma classe de defeito da §8f.
 */
function NovaContagem({ today }: { today: string }) {
  const criar = useCreateCountdown();
  // A chave zera o formulario depois de criar, sem reset campo a campo — que
  // esqueceria o proximo campo acrescentado aqui.
  const [rodada, setRodada] = useState(0);

  return (
    <FormularioDeContagem
      key={rodada}
      idBase="nova"
      titulo="Nova contagem"
      inicial={{
        title: '',
        targetDate: today,
        repeat: 'none',
        icon: null,
        color: COR_PADRAO_CONTAGEM,
        reminderTime: null,
        reminderDaysBefore: 0,
      }}
      rotuloDoBotao="Criar contagem"
      pendente={criar.isPending}
      onSubmit={(valores) => {
        criar.mutate({ id: newCountdownId(), ...valores });
        setRodada((n) => n + 1);
      }}
    />
  );
}

/** So o que mudou vai no PATCH — a auditoria guarda o `diff` recebido. */
function patchDeContagem(
  countdown: Countdown,
  valores: ValoresDaContagem,
): UpdateCountdownPayload {
  const patch: UpdateCountdownPayload = {};

  if (valores.title !== countdown.title) patch.title = valores.title;
  if (valores.targetDate !== countdown.targetDate) patch.targetDate = valores.targetDate;
  if (valores.repeat !== countdown.repeat) patch.repeat = valores.repeat;
  if (valores.icon !== countdown.icon) patch.icon = valores.icon;
  if (valores.color !== countdown.color) patch.color = valores.color;
  if (valores.reminderTime !== countdown.reminderTime) patch.reminderTime = valores.reminderTime;
  if (valores.reminderDaysBefore !== countdown.reminderDaysBefore) {
    patch.reminderDaysBefore = valores.reminderDaysBefore;
  }

  return patch;
}
